import { chatMessagesService } from '../services/chatMessages.service';
import { toPlainMessageText } from '../../utils/basicMessageFormat';

export const ReactionStatus = Object.freeze({
  PENDING_ADDITION: 'PENDING_ADDITION',
  PENDING_DELETION: 'PENDING_DELETION',
});

export const MessageStatus = Object.freeze({
  PENDING_CONFIRMATION: 'PENDING_CONFIRMATION',
  SEND_FAILED: 'SEND_FAILED',
});

const reactionIncludesUser = (reaction, reactedBy) => {
  return (reaction?.reactedBy || []).includes(reactedBy);
};

const reactionAlreadyExists = (message, reaction, reactedBy) =>
  (message?.reactions || []).some(
    r => r.reaction === reaction && reactionIncludesUser(r, reactedBy) && r.status !== ReactionStatus.PENDING_DELETION
  );

const removeReactionUser = (reaction, reactedBy) => {
  return (reaction?.reactedBy || []).filter(userId => userId !== reactedBy);
};

const normalizeReactedByIds = reactedBy => {
  if (Array.isArray(reactedBy)) {
    return reactedBy.filter(Boolean);
  }

  return reactedBy ? [reactedBy] : [];
};

const getMsgTime = m => {
  if (m.createdAt) return new Date(m.createdAt).getTime();
  return parseInt(String(m._id).slice(0, 8), 16) * 1000;
};

const getRouteChatId = () => {
  if (typeof window === 'undefined') return null;

  const matches = window.location.pathname.match(/^\/messages\/[^/]+\/([^/?#]+)/);
  return matches?.[1] ?? null;
};

const getLastActivityMessageType = message => {
  const messageType = message?.type || message?.messageType;

  if (messageType === 'Media') {
    return 'Photo';
  }

  if (messageType === 'File') {
    const fileType = String(message?.fileType || '').toUpperCase();
    return ['MP3', 'M4A'].includes(fileType) || message?.audioFileDuration || message?.duration ? 'Audio' : 'File';
  }

  return messageType;
};

const buildMessageLastActivity = message => {
  if (!message) return null;

  return {
    type: 'message',
    message: {
      messageId: message?._id,
      contentPreview: message?.contentPreview ?? toPlainMessageText(message?.content, message?.contentFormat),
      messageType: getLastActivityMessageType(message),
      senderId: message?.senderId ?? message?.sender?.userId ?? message?.sender?._id,
      isMessageFlagged: Boolean(message?.isMessageFlagged),
    },
  };
};

const buildReactionLastActivity = ({ message, reaction, reactedBy }) => {
  if (!message || !reaction || !reactedBy) return null;

  return {
    messageId: message?._id,
    reaction,
    reactedBy,
    messageType: getLastActivityMessageType(message),
    contentPreview: message?.contentPreview ?? toPlainMessageText(message?.content, message?.contentFormat),
  };
};

const buildReactionRemovedLastActivity = message => {
  return {
    type: 'message',
    message: {
      messageId: message?._id,
      contentPreview: 'Reaction removed',
      messageType: 'Text',
      senderId: message?.senderId ?? message?.sender?.userId ?? message?.sender?._id,
      isMessageFlagged: false,
    },
  };
};

const mergeDedupeSort = (existing, incoming) => {
  const map = new Map();
  for (const m of [...existing, ...incoming]) map.set(m._id, m);
  return Array.from(map.values()).sort((a, b) => getMsgTime(a) - getMsgTime(b));
};

const transformReactions = message => {
  const reactionMap = (message?.reactions || []).reduce((acc, { reaction, reactedBy, createdAt }) => {
    if (!reaction) return acc;

    if (!acc[reaction]) {
      acc[reaction] = { reaction, createdAt, reactedBy: [] };
    }

    acc[reaction].reactedBy.push(reactedBy);
    return acc;
  }, {});

  return {
    ...message,
    reactions: Object.values(reactionMap),
  };
};

const chatMessagesModel = {
  state: {
    messages: [],
    chatParticipants: [],
    replyBackMessage: null,
    hasMoreMessages: false,
    isLoading: false,
    isLoadingMoreMessages: false,
    isInitialLoading: false,
    selectedFocusMessage: null,
    focusMessageSafetyChoice: null,
    isFlaggingMessage: true,
    flagError: null,
    error: false,
    chatType: undefined,
    imageFullViewOpen: false,
    imageFullViewData: null,
    retryMessageId: null,
    retryAttempts: {},
  },
  reducers: {
    setData(state, payload) {
      return { ...state, ...payload };
    },
    markContentViewed(state, messageId) {
      return {
        ...state,
        messages: state.messages.map(message =>
          message?._id === messageId ? { ...message, isContentViewed: true } : message
        ),
      };
    },
    handleOptimisticReactionUpdate(state, payload) {
      const { messageId, reaction, reactedBy } = payload;

      return {
        ...state,
        messages: state.messages.map(m => {
          if (m._id !== messageId) return m;
          let updatedReactions = m.reactions || [];

          if (reactionAlreadyExists(m, reaction, reactedBy)) {
            updatedReactions = updatedReactions.map(r => {
              if (r.reaction === reaction && reactionIncludesUser(r, reactedBy)) {
                return {
                  ...r,
                  status: ReactionStatus.PENDING_DELETION,
                };
              }
              return r;
            });

            return {
              ...m,
              reactions: updatedReactions,
            };
          }

          const existingReactionIndex = updatedReactions.findIndex(r => r.reaction === reaction);

          if (existingReactionIndex >= 0) {
            updatedReactions = updatedReactions.map(r => {
              if (r.reaction !== reaction) return r;

              if (reactionIncludesUser(r, reactedBy)) {
                return r;
              }

              return {
                ...r,
                reactedBy: [...(r.reactedBy || []), reactedBy],
                status: ReactionStatus.PENDING_ADDITION,
              };
            });

            return {
              ...m,
              reactions: updatedReactions,
            };
          }

          return {
            ...m,
            reactions: [
              ...updatedReactions,
              { reaction: reaction, reactedBy: [reactedBy], status: ReactionStatus.PENDING_ADDITION },
            ],
          };
        }),
      };
    },
    rollbackReactionOnError(state, payload) {
      const { messageId, reaction, reactedBy } = payload;

      return {
        ...state,
        messages: state.messages.map(m => {
          if (m._id !== messageId) return m;

          const newReactions = (m.reactions || [])
            .map(r => {
              if (r.reaction === reaction && reactionIncludesUser(r, reactedBy)) {
                if (r.status === ReactionStatus.PENDING_ADDITION) {
                  const remainingUsers = removeReactionUser(r, reactedBy);
                  if (remainingUsers.length === 0) return null;

                  const rest = { ...r };
                  delete rest.status;
                  return {
                    ...rest,
                    reactedBy: remainingUsers,
                  };
                }
                if (r.status === ReactionStatus.PENDING_DELETION) {
                  const rest = { ...r };
                  delete rest.status;
                  return rest;
                }
              } else {
                return r;
              }
            })
            .filter(Boolean);

          return { ...m, reactions: newReactions };
        }),
      };
    },
    confirmReaction(state, payload) {
      const { messageId, reaction, reactedBy, reactedAt } = payload;

      return {
        ...state,
        messages: state.messages.map(m => {
          if (m._id !== messageId) return m;

          const newReactions = (m.reactions || [])
            .map(it => {
              if (it.reaction === reaction && reactionIncludesUser(it, reactedBy)) {
                if (it.status === ReactionStatus.PENDING_ADDITION) {
                  const rest = { ...it };
                  delete rest.status;
                  return {
                    ...rest,
                    createdAt: reactedAt,
                  };
                }
                if (it.status === ReactionStatus.PENDING_DELETION) {
                  const remainingUsers = removeReactionUser(it, reactedBy);
                  if (remainingUsers.length === 0) return null;

                  const rest = { ...it };
                  delete rest.status;
                  return {
                    ...rest,
                    reactedBy: remainingUsers,
                    createdAt: reactedAt || it.createdAt,
                  };
                }
              }

              return it;
            })
            .filter(Boolean);

          return { ...m, reactions: newReactions };
        }),
      };
    },
    appendPendingMessage(state, payload) {
      return {
        ...state,
        messages: [...state.messages, transformReactions(payload)],
      };
    },
    confirmMessage(state, payload) {
      const normalizedResponse = transformReactions(payload?.response || {});

      return {
        ...state,
        messages: state.messages.map(m => {
          if (m.clientMessageId !== payload.clientMessageId) return m;

          return {
            isAuthor: true,
            ...normalizedResponse,
          };
        }),
      };
    },
    setToSendFailed(state, payload) {
      return {
        ...state,
        messages: state.messages.map(m => {
          if (!m.clientMessageId || m.clientMessageId !== payload.clientMessageId) return m;

          return {
            ...m,
            status: MessageStatus.SEND_FAILED,
          };
        }),
      };
    },
    setToPendingConfirmation(state, payload) {
      return {
        ...state,
        messages: state.messages.map(m => {
          if (!m.clientMessageId || m.clientMessageId !== payload.clientMessageId) return m;

          return {
            ...m,
            status: MessageStatus.PENDING_CONFIRMATION,
          };
        }),
      };
    },
    setChatParticipants(state, participants) {
      return {
        ...state,
        chatParticipants: participants,
      };
    },
    addReactionToMessage(state, payload) {
      const { messageId, reaction, reactedBy, createdAt, lastActivityAt } = payload;
      const reactedByIds = normalizeReactedByIds(reactedBy);
      const reactionCreatedAt = createdAt || lastActivityAt;

      if (!reaction || reactedByIds.length === 0) {
        return state;
      }

      return {
        ...state,
        messages: state.messages.map(message => {
          if (message._id !== messageId) return message;

          const reactions = message.reactions ?? [];
          const existingReaction = reactions.find(r => r.reaction === reaction);

          if (existingReaction) {
            const currentReactedBy = normalizeReactedByIds(existingReaction.reactedBy);
            const nextReactedBy = Array.from(new Set([...currentReactedBy, ...reactedByIds]));

            if (nextReactedBy.length === currentReactedBy.length) {
              return message;
            }

            return {
              ...message,
              reactions: reactions.map(r =>
                r.reaction !== reaction
                  ? r
                  : {
                      ...r,
                      reactedBy: nextReactedBy,
                      createdAt: reactionCreatedAt || r.createdAt,
                    }
              ),
            };
          }

          return {
            ...message,
            reactions: [
              ...reactions,
              {
                reaction,
                reactedBy: reactedByIds,
                createdAt: reactionCreatedAt,
              },
            ],
          };
        }),
      };
    },
    removeReactionFromMessage(state, payload) {
      const { messageId, reaction, reactedBy } = payload;
      const reactedByIds = normalizeReactedByIds(reactedBy);

      if (!reaction || reactedByIds.length === 0) {
        return state;
      }

      return {
        ...state,
        messages: state.messages.map(message => {
          if (message._id !== messageId) return message;

          const nextReactions = (message.reactions ?? [])
            .map(r => {
              if (r.reaction !== reaction) return r;

              const remainingReactedBy = normalizeReactedByIds(r.reactedBy).filter(
                userId => !reactedByIds.includes(userId)
              );

              if (remainingReactedBy.length === 0) {
                return null;
              }

              return {
                ...r,
                reactedBy: remainingReactedBy,
              };
            })
            .filter(Boolean);

          return {
            ...message,
            reactions: nextReactions,
          };
        }),
      };
    },
    updateMessageStatus(state, payload) {
      const currentMessages = state.messages ?? [];

      if (currentMessages.length === 0) {
        return state;
      }

      if (!payload?.updates || payload.updates.length === 0) {
        return state;
      }

      const selectedChat = currentMessages[0].chatId;

      const messageIdsToUpdate = new Set(
        payload.updates.filter(update => update.chatId === selectedChat).map(update => update.messageId)
      );

      if (messageIdsToUpdate.size === 0) {
        return state;
      }

      const newMessages = currentMessages.map(m =>
        messageIdsToUpdate.has(m._id) ? { ...m, status: payload.newStatus } : m
      );

      return {
        ...state,
        messages: newMessages,
      };
    },
  },
  effects: dispatch => ({
    async fetchChatMessages(payload, rootState) {
      try {
        if (!payload?.chatId || getRouteChatId() !== payload.chatId) {
          return;
        }

        const currentMessages = rootState.chatMessagesModel.messages;
        this.setData({ isLoading: true });

        if (currentMessages.length === 0) {
          this.setData({ isInitialLoading: true });
        }

        const response = await chatMessagesService.fetchChatMessages(payload);
        const responsePayload = response?.data;

        if (getRouteChatId() !== payload.chatId) {
          return;
        }

        if (!responsePayload.data?.error) {
          const isSameChat = currentMessages[0]?.chatId === payload.chatId;
          const isSearch = Boolean(payload.search && payload.search.trim() !== '');

          const existingMessages = isSameChat && !isSearch ? currentMessages || [] : [];
          const incomingMessages = (responsePayload.data.messages || []).slice().reverse();
          const messagesWithTransformedReactions = incomingMessages.map(transformReactions);

          const mergedMessages = isSearch
            ? messagesWithTransformedReactions
            : mergeDedupeSort(existingMessages, messagesWithTransformedReactions);

          this.setData({
            messages: mergedMessages,
            hasMoreMessages: responsePayload.data.isNextPage,
            isLoading: false,
            error: false,
            isInitialLoading: false,
          });
        } else {
          this.setData({
            messages: [],
            error: true,
            isLoading: false,
            isInitialLoading: false,
          });
        }
      } catch (error) {
        this.setData({ error: true, isLoading: false, isInitialLoading: false });
      }
    },
    async fetchChatMessageByIdAndAdd(payload, rootState) {
      try {
        const { chatId, messageId } = payload;

        const currentMessages = rootState.chatMessagesModel.messages || [];

        const response = await chatMessagesService.fetchChatMessageById(chatId, messageId);
        const fetchedMessage = response?.data?.data;

        if (!fetchedMessage) return;

        const messageExists = currentMessages.some(m => m._id === fetchedMessage._id);

        if (messageExists) return;

        const nextMessages = [...currentMessages, fetchedMessage];

        this.setData({
          messages: nextMessages,
        });
      } catch (error) {
        console.error('Error fetching chat message:', error);
      }
    },
    async toggleReaction(payload, rootState) {
      const { chatId, messageId, reactedBy, reaction } = payload;
      if (!chatId || !messageId || !reactedBy || !reaction) {
        return;
      }

      try {
        const message = (rootState.chatMessagesModel.messages || []).find(m => m._id === messageId);
        const wasReactionAlreadyPresent = reactionAlreadyExists(message, reaction, reactedBy);

        this.handleOptimisticReactionUpdate({ messageId, reactedBy, reaction });

        const { data } = await chatMessagesService.toggleReaction(chatId, messageId, reactedBy, reaction);

        if (data?.error) {
          this.rollbackReactionOnError({ messageId, reactedBy, reaction });
          return;
        }

        this.confirmReaction({ messageId, reactedBy, reaction, reactedAt: data.data.reactedAt });

        if (!wasReactionAlreadyPresent) {
          const lastActivity = buildReactionLastActivity({
            message,
            reaction,
            reactedBy,
          });

          if (lastActivity) {
            dispatch.chatsModel.updateReactionLastActivityAndMoveChatToTop({
              chatId,
              ...lastActivity,
              lastActivityAt: data.data.reactedAt,
            });
          }
        } else {
          dispatch.chatsModel.updateMessageLastActivityAndMoveChatToTop({
            chatId,
            lastActivity: buildReactionRemovedLastActivity(message),
            lastActivityAt: data.data.reactedAt,
          });
        }
      } catch (err) {
        this.rollbackReactionOnError({ messageId, reactedBy, reaction });
      }
    },
    async sendMessage(payload) {
      const clientMessageId = crypto.randomUUID();

      try {
        if (payload.isRetry) {
          this.setToPendingConfirmation({ clientMessageId: payload.clientMessageId });
        } else {
          const tempFileUrl = ['File', 'Media'].includes(payload.messageType)
            ? `https://${clientMessageId}.com/iMentor/${payload?.fileType}/${payload?.file?.name
                .slice(0, -1 * (payload?.fileType?.length + 1))
                .slice(0, 15)
                .replace(/\s/g, '_')}_${Math.floor(Math.random() * 100)}.${payload?.fileType.toLowerCase()}`
            : undefined;

          const pendingMessagePayload = {
            clientMessageId: clientMessageId,
            content: payload.content,
            type: payload.messageType,
            category: payload.category,
            isAuthor: true,
            createdAt: new Date().toISOString(),
            status: MessageStatus.PENDING_CONFIRMATION,
            reactions: [],
            ...(['File', 'Media'].includes(payload.messageType) &&
              payload?.fileType && {
                fileUrl: tempFileUrl,
                file: payload.file,
              }),
            ...(payload.messageType === 'File' && payload?.duration && { audioFileDuration: payload.duration }),
            ...(payload.messageType === 'Quote' &&
              payload?.quotedMessage && {
                quote: payload.quotedMessage,
              }),
            ...(payload.messageType === 'Content' &&
              payload?.contentDetails && {
                contentId: payload.contentId,
                contentDetails: payload.contentDetails,
              }),
          };

          this.appendPendingMessage(pendingMessagePayload);
        }

        const { data } = await chatMessagesService.sendMessage(payload);

        if (data?.error) {
          this.setToSendFailed({ clientMessageId: payload.isRetry ? payload.clientMessageId : clientMessageId });
          return;
        }

        this.confirmMessage({
          clientMessageId: payload.isRetry ? payload.clientMessageId : clientMessageId,
          response: data?.data,
        });

        const lastActivity = buildMessageLastActivity(data?.data);

        if (lastActivity && data?.data?.chatId) {
          dispatch.chatsModel.updateMessageLastActivityAndMoveChatToTop({
            chatId: data.data.chatId,
            lastActivity,
            lastActivityAt: data.data.createdAt || new Date().toISOString(),
          });
        }
      } catch (err) {
        this.setToSendFailed({ clientMessageId: payload.isRetry ? payload.clientMessageId : clientMessageId });
      }
    },
    async fetchChatParticipants(chatId) {
      try {
        const { data } = await chatMessagesService.fetchChatParticipants(chatId);

        if (data?.error) {
          this.setData({ error: true });
          return;
        }

        this.setChatParticipants(data?.data);
      } catch (err) {
        this.setData({ error: true });
      }
    },
  }),
};

export default chatMessagesModel;

export const hasMessage = (state, messageId) => {
  return state.chatMessagesModel.messages.some(m => m._id === messageId);
};
